/**
 * Object tracking payload helpers.
 *
 * Boxes are stored as fractions of the composed canvas, which means their
 * numbers depend on the story's aspect ratio - the same box over the same face
 * reads differently on a 16:9 story than on a 9:16 one, because the video sits
 * in a different part of the canvas. The tracker works on the video itself, so
 * every box is re-based into the video's own 0..1 box before it is sent, with
 * the crop zoom and offset undone on the way.
 */
import app from 'app';
import { getAssetRectOnStage } from 'AssetEditor/store/assetEditorApi.js';

export const AREA_FIELD_TYPE = 'AREA';
export const TRACKER_TYPE = 'CSRT';
export const TRACKING_INTERVAL = 1;

/**
 * The field definitions of a custom overlay, read from the theme preset.
 *
 * `page.fields` is deliberately NOT the source: CustomOverlaysFields#toggleExpend
 * writes its normalized (and area-excluded) list back through the updatePage
 * mutation, so a page can lose its AREA fields once a collapsible section is
 * toggled. The preset is never mutated.
 *
 * @param currentPage {Object}
 * @param presets {Array} themeInfo presets
 * @return {Array}
 */
export function getPresetFields(currentPage, presets) {
  const preset = _.find(presets || [], { id: currentPage && currentPage.id });

  return (preset && preset.fields) || (currentPage && currentPage.fields) || [];
}

/**
 * AREA fields that ask to be tracked, in preset declaration order.
 *
 * @param fields {Array}
 * @return {Array}
 */
export function getTrackedAreaFields(fields) {
  return _.filter(
    fields || [],
    (field) => field.type === AREA_FIELD_TYPE && field.trackArea === true
  );
}

/**
 * Whether an AREA field takes part in the show/hide toggle at all.
 *
 * Fields that opt out must never get an `active` property written to the story -
 * not `null`, not `false`, no key.
 *
 * @param field {Object} preset field
 * @return {Boolean}
 */
export function hasActiveControl(field) {
  return (
    !!field &&
    (field.showActiveControl === true || _.isBoolean(field.defaultActive))
  );
}

/**
 * Whether a field is currently shown: the AGD value if it has one, otherwise the
 * preset default. `undefined` when the field has no active concept, so callers
 * can tell "not applicable" from "hidden".
 *
 * @param field {Object} preset field
 * @param coFields {Array} fields of the custom overlay, as stored in the AGD
 * @return {Boolean|undefined}
 */
export function resolveActive(field, coFields) {
  if (!hasActiveControl(field)) {
    return undefined;
  }

  const fromAgd = _.find(coFields || [], { name: field.name });

  return _.isBoolean(fromAgd && fromAgd.active)
    ? fromAgd.active
    : field.defaultActive !== false;
}

/**
 * A field with no active concept counts as shown.
 *
 * @param field {Object} preset field
 * @param coFields {Array}
 * @return {Boolean}
 */
export function isFieldActive(field, coFields) {
  return resolveActive(field, coFields) !== false;
}

/**
 * Same question asked of a single field object that already carries its stored
 * state - the left panel and the stage picker both work with merged fields
 * rather than a (preset, agd fields) pair.
 *
 * @param field {Object}
 * @return {Boolean}
 */
export function isFieldShown(field) {
  return _.isBoolean(field && field.active)
    ? field.active
    : (field && field.defaultActive) !== false;
}

/**
 * Tracked fields the user can actually see - a hidden area is not tracked.
 *
 * @param trackedFields {Array}
 * @param coFields {Array}
 * @return {Array}
 */
export function getActiveTrackedFields(trackedFields, coFields) {
  return _.filter(trackedFields || [], (field) =>
    isFieldActive(field, coFields)
  );
}

/**
 * Whether the asset can be tracked at all - the server guards on
 * VideoFragmentMetaAsset, and SocialFragmentMetaAsset does not extend it, so
 * social fragments are out.
 *
 * @param workModel {Object}
 * @return {Boolean}
 */
export function isTrackableAsset(workModel) {
  return (
    !!workModel &&
    app.assetType.isFRAGMENT(workModel) &&
    !app.assetType.isSOCIALFRAGMENT(workModel)
  );
}

/**
 * Whether the asset carries a collage/scene layout, which changes where the main
 * video is drawn and where its crop lives.
 *
 * @param workModel {Object}
 * @return {Boolean}
 */
export function hasSceneLayout(workModel) {
  return _.some(
    (workModel && workModel.overlayTracks) || [],
    (overlay) => !!(overlay && overlay.sceneLayout)
  );
}

/**
 * Where the tracked video sits inside the editor stage, so canvas coordinates
 * can be expressed in the video's own.
 *
 * @param workModel {Object}
 * @param stageDimensions {Object} see getStageDimensions
 * @return {Object|null} null when the asset's dimensions are unknown
 */
export function getTrackedAssetRect(workModel, stageDimensions) {
  if (!workModel || hasSceneLayout(workModel)) {
    // a collage lays the main video out against the layout's bounding box, not
    // the whole canvas, and its crop is written to the overlay track's sceneAgd
    // rather than here (see setCropData). Neither is modelled below, so refuse
    // rather than convert against the wrong rectangle.
    return null;
  }

  const agd = workModel.agd || {};

  return getAssetRectOnStage({
    metadata: workModel.metadata,
    // the real crop, not the zoom page's temporary override
    scale: agd.scale,
    offset: agd.offset,
    stageDimensions,
    isSocial:
      app.assetType.isSOCIAL(workModel) ||
      app.assetType.isSOCIALFRAGMENT(workModel),
    isWebpage: app.assetType.isWEBPAGE(workModel),
  });
}

/**
 * Whether a tracked field would produce the same tracking request.
 *
 * Compares the parsed geometry rather than the raw value string, because merely
 * selecting a box rewrites its zIndex (CustomOverlaysAreaPicker activeOverlay
 * watcher) without moving it. Hiding or showing the area counts as a change too,
 * since a hidden area is not tracked.
 *
 * Canvas geometry, deliberately, even though the payload is now a function of
 * the crop as well: changing the crop is a product decision NOT to re-track. The
 * consequence is that a stored id can describe the region the box covered under
 * the previous crop. Comparing converted boxes would not help - both sides would
 * convert through the same current rectangle and still match; catching it would
 * mean persisting the crop the job was created with.
 *
 * @param field {Object} preset field
 * @param coFields {Array} current AGD fields
 * @param previousFields {Array} AGD fields as of the last apply
 * @return {Boolean}
 */
export function isSameTrackedArea(field, coFields, previousFields) {
  return (
    isFieldActive(field, coFields) === isFieldActive(field, previousFields) &&
    _.isEqual(
      toBoundingBox(field.name, getFieldValue(field, coFields)),
      toBoundingBox(field.name, getFieldValue(field, previousFields))
    )
  );
}

/**
 * The second the boxes were positioned on - where the stage playhead sits when
 * the user drags a box.
 *
 * RELATIVE to the start of the segment: 0 is the segment's first frame, not
 * second 0 of the source video.
 *
 * @param timeInAsset {Number} playhead position, 0..1 of the segment
 * @param displayDuration {Number} length of the segment in seconds
 * @return {Number}
 */
export function getReferenceTime(timeInAsset, displayDuration) {
  const position = parseFloat(timeInAsset);
  const duration = parseFloat(displayDuration);

  if (!_.isFinite(position) || !_.isFinite(duration)) {
    return 0;
  }

  return app.sfp(position * duration, 1e3);
}

/**
 * The tracked video segment, in source video seconds.
 * `startTime`/`endTime` are absent on assets that were never trimmed.
 *
 * @param workModel {Object}
 * @return {{startTime: Number, endTime: Number}}
 */
export function getSegmentTimes(workModel) {
  const startTime = parseFloat(workModel && workModel.startTime);
  const endTime = parseFloat(workModel && workModel.endTime);

  if (_.isFinite(startTime) && _.isFinite(endTime) && startTime !== endTime) {
    return { startTime, endTime };
  }

  const duration = parseFloat(
    workModel && workModel.metadata && workModel.metadata.duration
  );

  return { startTime: 0, endTime: _.isFinite(duration) ? duration : 0 };
}

/**
 * The current value of a custom overlay field, falling back to the preset
 * default when the field was never written to the AGD.
 *
 * @param field {Object} preset field
 * @param coFields {Array} fields of the custom overlay, as stored in the AGD
 * @return {String|undefined}
 */
export function getFieldValue(field, coFields) {
  const fromAgd = _.find(coFields || [], { name: field.name });
  const value = fromAgd && fromAgd.value;

  return _.isUndefined(value) || _.isNull(value) ? field.defaultValue : value;
}

/**
 * `"x y w h zIndex"` -> a named bounding box. Returns null when the value
 * cannot be parsed, so a malformed field never reaches the server as nulls.
 *
 * @param name {String}
 * @param value {String}
 * @return {Object|null}
 */
export function toBoundingBox(name, value) {
  const [x, y, w, h] = String(value || '')
    .split(' ')
    .map((n) => parseFloat(n));

  if (![x, y, w, h].every((n) => _.isFinite(n))) {
    return null;
  }

  return { name, x, y, w, h };
}

/**
 * Re-base a canvas-space box into the video's own 0..1 box.
 *
 * The stored numbers are fractions of the whole composed canvas, so a box over
 * the video's top-left corner reads as 0.219, 0 on a 16:9 story holding a square
 * video - the letterbox space either side is included. The tracker works on the
 * video, where that same corner is 0, 0.
 *
 * Values are deliberately NOT clamped to 0..1. A box can hang over the letterbox
 * space beside the video, or off the frame once the video is zoomed, and that is
 * reported honestly as a negative coordinate or one past 1 - clipping it to the
 * frame would shrink the region the user actually drew and move its centre.
 *
 * @param box {Object} { name, x, y, w, h } in canvas fractions
 * @param rect {Object} the video's rectangle in stage pixels
 * @param stageDimensions {Object} see getStageDimensions
 * @return {Object|null}
 */
export function toVideoSpace(box, rect, stageDimensions) {
  if (!box || !rect || !rect.width || !rect.height || !stageDimensions) {
    return null;
  }

  const { assetEditorWidth, assetEditorHeight } = stageDimensions;

  return {
    name: box.name,
    x: app.sfp((box.x * assetEditorWidth - rect.left) / rect.width, 1e4),
    y: app.sfp((box.y * assetEditorHeight - rect.top) / rect.height, 1e4),
    w: app.sfp((box.w * assetEditorWidth) / rect.width, 1e4),
    h: app.sfp((box.h * assetEditorHeight) / rect.height, 1e4),
  };
}

/**
 * Build the POST body.
 *
 * `userId` and `videoUrl` are deliberately absent: the server overrides the
 * user with the authenticated one, and resolves the video itself from the
 * assetUUID in the path.
 *
 * @param trackedFields {Array} preset AREA fields with trackArea
 * @param coFields {Array} fields of the custom overlay, as stored in the AGD
 * @param workModel {Object}
 * @return {Object|null} null when there is nothing to track
 */
export function buildTrackingPayload({
  trackedFields,
  coFields,
  workModel,
  timeInAsset,
  stageDimensions,
  assetRect,
}) {
  const rect = assetRect;

  if (!rect) {
    return null;
  }

  const boxes = _.compact(
    _.map(getActiveTrackedFields(trackedFields, coFields), (field) =>
      toVideoSpace(
        toBoundingBox(field.name, getFieldValue(field, coFields)),
        rect,
        stageDimensions
      )
    )
  );

  if (_.isEmpty(boxes)) {
    return null;
  }

  const { startTime, endTime } = getSegmentTimes(workModel);

  return {
    boxes,
    interval: TRACKING_INTERVAL,
    referenceTime: getReferenceTime(timeInAsset, workModel.displayDuration),
    startTime,
    endTime,
    tracker: TRACKER_TYPE,
  };
}
